import { ChatConversationMessage, Meeting } from '../models/chat';
import { AbstractStore, ActionHandlers, BaseStoreAction } from './abstract.store';
import { CacheService } from './cache.service';

export type ChatMessageUpload = {
    hash: any;
    name: string;
    progress: string;
    remote_id?: string;
    promise?: Promise<ChatMessageUpload>;
};

export type ChatMessageInputState = {
    content: string;
    quoted_message?: ChatConversationMessage;
    editedMessage?: ChatConversationMessage;
    uploads: ChatMessageUpload[];
};

export type ChatMessageInputStoreState = {
    [conversationId: number]: ChatMessageInputState;
};

export class ChatMessageInputStore extends AbstractStore<BaseStoreAction, ChatMessageInputStoreState> {
    private log(...args: any[]) {
        console.log.apply(console, ['ChatMessageInputStore', ...args]);
    }

    static $inject = ['CacheService'];

    constructor(private cacheService: CacheService) {
        super();

        const cacheKey = 'ChatMessageInputStore';

        this.dispatch({ type: 'set_state', payload: this.cacheService.fromCache(cacheKey, this.createInitialState()) });

        this.getStore$()
            .debounce(10)
            .subscribe((state) => {
                const serialized = this.serialize(state);
                this.cacheService.toCache(cacheKey, serialized);
            });
    }

    private serialize(state: ChatMessageInputStoreState) {
        return Object.keys(state).reduce((previousValue, key) => {
            const inputState: ChatMessageInputState = state[key as any];

            return {
                ...previousValue,
                [key]: {
                    ...inputState,
                    uploads: [],
                    // only save already uploaded documents
                    // todo: presist uploads as drafts
                    // todo (?) what if uploads will be automatically removed
                    // uploads: inputState.uploads.filter(upload => {
                    //   console.log(upload);
                    //   return Boolean(upload.remote_id)
                    // })
                },
            };
        }, state);
    }

    createInitialState(): ChatMessageInputStoreState {
        return {};
    }

    getActionHandlers(): ActionHandlers<BaseStoreAction, ChatMessageInputStoreState> {
        return {
            set_state: (state, payload) => ({ ...payload }),
            set_input_state: (state, { conversationId, inputState }) => ({
                ...state,
                [conversationId]: {
                    ...this.createEmptyMessageInputState(),
                    ...inputState,
                },
            }),
            update_input_state: (state, { conversationId, inputState }) => ({
                ...state,
                [conversationId]: {
                    ...(state[conversationId] || this.createEmptyMessageInputState()),
                    ...inputState,
                },
            }),

            remove_upload: (state, { conversationId, hash }) => {
                const conversation = state[conversationId] || this.createEmptyMessageInputState();
                return {
                    ...state,
                    [conversationId]: {
                        ...conversation,
                        uploads: conversation.uploads.filter((u) => u.hash !== hash),
                    },
                };
            },
            update_upload: (state, { conversationId, upload }) => {
                const conversation = state[conversationId] || this.createEmptyMessageInputState();
                return {
                    ...state,
                    [conversationId]: {
                        ...conversation,
                        uploads: conversation.uploads.map((u) => (u.hash === upload.hash ? upload : u)),
                    },
                };
            },
            add_upload: (state, { conversationId, upload }) => {
                const conversation = state[conversationId] || this.createEmptyMessageInputState();

                return {
                    ...state,
                    [conversationId]: {
                        ...conversation,
                        uploads: [...conversation.uploads, upload],
                    },
                };
            },
        };
    }

    private createEmptyMessageInputState(): ChatMessageInputState {
        return {
            content: '',
            quoted_message: undefined,
            editedMessage: undefined,
            uploads: [],
        };
    }

    dispatchSetInputState(conversationId: number, inputState: Partial<ChatMessageInputState>) {
        this.dispatch({ type: 'set_input_state', payload: { conversationId, inputState } });
    }

    dispatchUpdateInputState(conversationId: number, inputState: Partial<ChatMessageInputState>) {
        this.dispatch({ type: 'update_input_state', payload: { conversationId, inputState } });
    }

    dispatchRemoveUpload(conversationId: number, hash: any) {
        this.dispatch({ type: 'remove_upload', payload: { conversationId, hash } });
    }

    dispatchUpdateUpload(conversationId: number, upload: ChatMessageUpload) {
        this.dispatch({ type: 'update_upload', payload: { conversationId, upload } });
    }

    dispatchAddUpload(conversationId: number, upload: ChatMessageUpload) {
        this.dispatch({ type: 'add_upload', payload: { conversationId, upload } });
    }

    getMessageInputState$(conversationId: number) {
        return this.store$.map((s) => s[conversationId]).filter((v) => Boolean(v));
    }

    hasDraft$(meeting: Meeting) {
        return this.getMessageInputState$(meeting.id).map((inputState) => {
            return Boolean(
                inputState.quoted_message || inputState.editedMessage || inputState.content || inputState.uploads.length
            );
        });
    }
}

angular.module('tradersyard').service('ChatMessageInputStore', ChatMessageInputStore);
